import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { fetchCurrentUser, login as loginRequest, register as registerRequest } from '../api/services/authService';

const TOKEN_KEY = 'rf_token';
const EMAIL_KEY = 'rf_email';
const USER_ID_KEY = 'rf_user_id';
const ROLES_KEY = 'rf_roles';
const GROUPS_KEY = 'rf_groups';
const NAME_KEY = 'rf_display_name';
const IS_OIDC_USER_KEY = 'rf_is_oidc_user';

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) || '');
  const userEmail = ref(localStorage.getItem(EMAIL_KEY) || '');
  const userId = ref(localStorage.getItem(USER_ID_KEY) ? Number(localStorage.getItem(USER_ID_KEY)) : null);
  const roles = ref(JSON.parse(localStorage.getItem(ROLES_KEY) || '[]'));
  const groups = ref(JSON.parse(localStorage.getItem(GROUPS_KEY) || '[]'));
  const displayName = ref(localStorage.getItem(NAME_KEY) || '');
  const isOIDCUser = ref(localStorage.getItem(IS_OIDC_USER_KEY) === 'true');
  const isInitialized = ref(false);
  const loading = ref(false);

  const isAuthenticated = computed(() => Boolean(token.value));
  const isSysadmin = computed(() => roles.value.includes('Sysadmin'));

  function persist() {
    if (token.value) {
      localStorage.setItem(TOKEN_KEY, token.value);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }

    if (userEmail.value) {
      localStorage.setItem(EMAIL_KEY, userEmail.value);
    } else {
      localStorage.removeItem(EMAIL_KEY);
    }

    if (userId.value !== null && userId.value !== undefined) {
      localStorage.setItem(USER_ID_KEY, String(userId.value));
    } else {
      localStorage.removeItem(USER_ID_KEY);
    }

    localStorage.setItem(ROLES_KEY, JSON.stringify(roles.value));
    localStorage.setItem(GROUPS_KEY, JSON.stringify(groups.value));
    if (displayName.value) {
      localStorage.setItem(NAME_KEY, displayName.value);
    } else {
      localStorage.removeItem(NAME_KEY);
    }

    if (isOIDCUser.value) {
      localStorage.setItem(IS_OIDC_USER_KEY, 'true');
    } else {
      localStorage.removeItem(IS_OIDC_USER_KEY);
    }
  }

  function clearSession() {
    token.value = '';
    userEmail.value = '';
    userId.value = null;
    roles.value = [];
    groups.value = [];
    displayName.value = '';
    isOIDCUser.value = false;
    persist();
  }

  async function refreshIdentity() {
    if (!token.value) {
      clearSession();
      isInitialized.value = true;
      return;
    }
    loading.value = true;
    try {
      const current = await fetchCurrentUser();
      userId.value = current.id;
      userEmail.value = current.email || userEmail.value;
      roles.value = (current.roles || []).map((role) => role.name);
      groups.value = (current.groups || [])
        .filter((group) => !group.archiviert)
        .map((group) => ({ id: group.id, name: group.name }));
      displayName.value = current.display_name || current.email || userEmail.value;
      isOIDCUser.value = current.is_oidc_user || false;
    } catch (error) {
      clearSession();
      throw error;
    } finally {
      loading.value = false;
      isInitialized.value = true;
      persist();
    }
  }

  async function initialize() {
    if (isInitialized.value) return;
    if (token.value) {
      try {
        await refreshIdentity();
      } catch (error) {
        console.error('Failed to restore session', error);
      }
    } else {
      isInitialized.value = true;
    }
  }

  async function login(email, password, rememberMe = false) {
    const data = await loginRequest(email, password, rememberMe);
    token.value = data.access_token;
    userEmail.value = email;
    displayName.value = email;
    isOIDCUser.value = false; // Traditional login, so not an OIDC user

    // Store remember me preference
    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }

    persist();
    await refreshIdentity();
  }

  async function register(email, password, displayNameInput) {
    await registerRequest({
      email,
      password,
      display_name: displayNameInput || email,
    });
    await login(email, password, false);
  }

  async function completeOidcLogin(accessToken) {
    if (!accessToken) {
      throw new Error('OIDC access token fehlt');
    }

    // Decode JWT token to extract email (without verification - backend already verified it)
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      userEmail.value = payload.sub; // JWT 'sub' claim contains the email
    } catch (e) {
      console.error('Failed to decode JWT token', e);
      throw new Error('Invalid OIDC token format');
    }

    token.value = accessToken;
    isOIDCUser.value = true;
    localStorage.setItem('rememberMe', 'true');
    persist();

    try {
      await refreshIdentity();
    } catch (error) {
      clearSession();
      throw error;
    }
  }

  function logout() {
    clearSession();
    isInitialized.value = true;
  }

  return {
    token,
    userEmail,
    userId,
    roles,
    groups,
    displayName,
    isOIDCUser,
    isInitialized,
    isAuthenticated,
    isSysadmin,
    loading,
    initialize,
    refreshIdentity,
    login,
    register,
    completeOidcLogin,
    logout,
  };
});
